import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import { getMultiFactorLabel } from '../../utils/firebaseMultiFactor'

interface Props {
  errorMessage: string | null
  hints: Firebase.auth.MultiFactorInfo[]
  isResolving: boolean
  isSendingCode: boolean
  onClose: () => void
  onConfirm: () => void
  onSendCode: () => void
  onVerificationCodeChange: (value: string) => void
  onSelectedHintUidChange: (value: string) => void
  recaptchaContainerId: string
  selectedHintUid: string
  verificationCode: string
  verificationId: string | null
}

export const LoginMultiFactorDialog: React.FC<Props> = ({
  errorMessage,
  hints,
  isResolving,
  isSendingCode,
  onClose,
  onConfirm,
  onSendCode,
  onVerificationCodeChange,
  onSelectedHintUidChange,
  recaptchaContainerId,
  selectedHintUid,
  verificationCode,
  verificationId,
}) => {
  const hasHints = hints.length > 0
  const codeRequested = verificationId != null

  return (
    <Dialog fullWidth maxWidth="sm" open onClose={onClose}>
      <DialogTitle>Two-Step Verification</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Enter your second verification factor to finish signing in.
          </Typography>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {hasHints ? (
            <FormControl fullWidth>
              <InputLabel id="mfa-factor-label">Verification method</InputLabel>
              <Select
                labelId="mfa-factor-label"
                label="Verification method"
                value={selectedHintUid}
                onChange={(event) =>
                  onSelectedHintUidChange(String(event.target.value))
                }
              >
                {hints.map((hint) => (
                  <MenuItem key={hint.uid} value={hint.uid}>
                    {getMultiFactorLabel(hint)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Alert severity="warning">
              This account requires a second factor that is not supported by
              this client build.
            </Alert>
          )}
          {codeRequested ? (
            <TextField
              autoFocus
              fullWidth
              label="Verification code"
              value={verificationCode}
              onChange={(event) => onVerificationCodeChange(event.target.value)}
            />
          ) : null}
          <Box id={recaptchaContainerId} sx={{ minHeight: 1 }} />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          pb: 2,
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={onSendCode}
            disabled={!hasHints || isSendingCode || isResolving}
          >
            {codeRequested ? 'Resend code' : 'Send code'}
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={!codeRequested || !verificationCode.trim() || isResolving}
          >
            Verify
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
